import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import { Mail, UserPlus } from "lucide-react";
import {
  Button,
  Input,
  Label,
  StatusStepper,
  SubmitButton,
  SubmittingHint,
  useSubmittingMutation,
  type StepperStep,
} from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { PortalApiError } from "../../api/client";
import {
  prequalSiteApi,
  type PrequalRole,
  type SendPrequalInvite,
} from "../../api/prequalSite";
import {
  SELECT_CLASS,
  errorMessage,
  type NewPrequalificationWizardProps,
} from "./panelProps";

// Partner Brand Ownership — the ONE way a partner starts a prequalification.
//
// This replaces the always-open invite form that used to sit above the lists.
// That form asked for the recipient and the delivery method in a single glance,
// with two competing submit buttons side by side, and it was the first thing on
// the tab whether or not anyone was being invited. Splitting it into WHO then
// HOW does three things at once: the identity is captured once and reused by
// both paths, the two paths stop competing for the same primary position, and
// the tab opens on the lists rather than on a form.
//
// Two steps, and a THIRD that only exists when the server says so:
//
//   1. WHO     — first name, last name, email, and the buyer/seller track.
//   2. HOW     — email them a link, or start it here with them in the room.
//   3. CONFIRM — a colleague already has a live invite out to this person.
//
// Step 3 is a STEP, not a nested dialog. A modal inside a modal, in a shared
// Modal with no focus management of its own, is a genuine keyboard trap: the
// inner overlay paints on top while Tab still walks the outer one. Making the
// warning the wizard's own third step keeps one dialog, one focus scope, and
// one Back button.
//
// Portal-local by the isolation rule: the design-system kit, the shared Modal
// (`@/components/ui/` is not one of isolation.test.ts's forbidden prefixes —
// live precedent is portal/pages/BrokerDealsPage.tsx), and portal-relative
// imports. No React Query; writes go through useSubmittingMutation.

type WizardStep = "who" | "how" | "duplicate";

const BASE_STEPS: StepperStep[] = [
  { key: "who", label: "Who", hint: "Who is prequalifying, and on which track." },
  { key: "how", label: "How", hint: "Email them a link, or start it here with them." },
];

/** Appended only while the server has actually raised the conflict. A step
 *  drawn before it can be reached promises a decision that usually never
 *  arrives. */
const CONFIRM_STEP: StepperStep = {
  key: "duplicate",
  label: "Confirm",
  hint: "Someone at your firm already invited this person.",
};

const STEP_INDEX: Record<WizardStep, number> = { who: 0, how: 1, duplicate: 2 };

/** What Tab may reach. Deliberately no visibility filter: jsdom reports no
 *  layout, so an `offsetParent` test would empty this list in every test while
 *  looking correct in a browser. Only the current step is rendered anyway —
 *  there is nothing hidden inside the wrapper to exclude. */
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

export function NewPrequalificationWizard({
  open,
  onClose,
  siteName,
  onSent,
}: NewPrequalificationWizardProps) {
  const navigate = useNavigate();

  const [step, setStep] = useState<WizardStep>("who");
  const [role, setRole] = useState<PrequalRole>("Buyer");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);

  // A colleague at this firm already has a live invite out to this person. Held
  // as the server's own message: it is composed there precisely so the warning
  // shown before the click and the error on a refused retry cannot drift.
  //
  // Only the MESSAGE, not the typed conflict payload the staff surface reads.
  // PortalApiError carries status + errors and has no `details` channel at all,
  // unlike the staff ApiError — and the message is the whole of what this step
  // renders, so widening the shared portal client for a list nothing displays
  // would be churn.
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);

  // Which action the confirm step should re-run. An in-office start and a
  // mailed invite both mint an invite and both hit the same warning, so the
  // step has to remember which one the partner pressed. Carried over verbatim
  // from the form this replaced — it is not incidental state.
  const [pendingAction, setPendingAction] = useState<"send" | "in-person">("send");

  // Every open starts clean. The wizard stays mounted while closed (the shell
  // renders it unconditionally and Modal returns null), so without this an
  // abandoned draft would still be sitting there on the next open.
  useEffect(() => {
    if (!open) return;
    setStep("who");
    setRole("Buyer");
    setFirstName("");
    setLastName("");
    setEmail("");
    setError(null);
    setDuplicateWarning(null);
    setPendingAction("send");
  }, [open]);

  // ── The wire body ─────────────────────────────────────────────────────────
  // Identical on both paths, and identical to what the form this replaced sent.
  //
  // No site, no brand, no broker id. The server resolves the caller's site from
  // the authenticated session, and a partner has no site to choose. The request
  // record does carry partnerBrandId / phoenixNative for the STAFF path; naming
  // either here would imply a choice this caller does not have.
  const inviteBody = useCallback(
    (confirmDuplicate: boolean): SendPrequalInvite => ({
      role,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      confirmDuplicate,
    }),
    [role, firstName, lastName, email],
  );

  // Duplicate detection is brand-scoped server-side, so a warning here can only
  // ever name someone on THIS firm's site. It can never reveal that another
  // firm is working the same buyer.
  const handleFailure = useCallback((err: unknown, fallback: string) => {
    if (err instanceof PortalApiError && err.status === 409) {
      setError(null);
      setDuplicateWarning(err.message);
      setStep("duplicate");
      return;
    }
    // Everything else stays on the step that raised it, with the wizard OPEN
    // and the typed values intact. That matters most for the daily-cap 429: it
    // is a terminal refusal whose message tells the partner to ask their
    // Phoenix contact to send on their behalf, and closing the wizard would
    // take away the address they need in order to do that.
    setError(errorMessage(err, fallback));
  }, []);

  const send = useSubmittingMutation(
    (confirmDuplicate: boolean) => prequalSiteApi.sendInvite(inviteBody(confirmDuplicate)),
    {
      onSuccess: () => {
        // Closed on the RAW prop, not the guarded wrapper below: `pending` is
        // still true while onSuccess runs, so the mid-submit guard would
        // swallow this and strand the partner on a wizard that has already
        // succeeded.
        //
        // Close BEFORE handing off. The invite is away; a throw out of the
        // shell's refresh must not leave it sitting behind a message saying it
        // failed. There is no success banner here — the shell switches to
        // Invitations, where the invite itself is the confirmation.
        onClose();
        void onSent();
      },
      onError: (err) => handleFailure(err, "Couldn't send that invitation. Try again."),
    },
  );

  // The SAME details, for a client who is sitting with you instead of on the
  // end of an email.
  //
  // It goes through the session-authenticated start, which mints a SUPPRESSED
  // invite. That is not a formality: without the invite the draft would be
  // ORGANIC — no sender, invisible on this partner's own In-progress list, and
  // reaching Phoenix as an abandoned stranger. Nothing is emailed, and because
  // it is the same body it passes through the same duplicate gate.
  const startInPerson = useSubmittingMutation(
    (confirmDuplicate: boolean) =>
      prequalSiteApi.startInOfficeDraft(inviteBody(confirmDuplicate)),
    {
      onSuccess: (started) => {
        // No onSent: this navigates away, and the In-progress list re-reads on
        // its next mount. Close first so Modal's scroll lock is released before
        // the route changes.
        onClose();
        navigate(`/portal/prequal-site/assist/${started.draftId}`);
      },
      onError: (err) =>
        handleFailure(err, "Couldn't start that prequalification. Try again."),
    },
  );

  // ONE flag for both paths. The form this replaced disabled its fields on
  // `send.pending` alone while its buttons used the combined flag, so an
  // in-person start left every field editable underneath it.
  const busy = send.pending || startInPerson.pending;

  const ready =
    firstName.trim().length > 0 && lastName.trim().length > 0 && email.trim().length > 0;

  // ── Focus ─────────────────────────────────────────────────────────────────
  // Modal has no focus management of any kind — no initial focus, no trap, no
  // restore — and it has ~230 importers and no owner, so the handling lives
  // here rather than in it.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const stepFocusRef = useRef<HTMLElement | null>(null);

  /** One callback ref, re-pointed by whichever step is rendering, at that
   *  step's first field. A callback (rather than three typed RefObjects) is
   *  what lets the same ref land on an input, a button, and a div. */
  const setStepFocus = useCallback((node: HTMLElement | null) => {
    stepFocusRef.current = node;
  }, []);

  useEffect(() => {
    if (!open) return;
    // Effects run after commit, so the portaled step body is already in the
    // document and the ref points at this step's first field.
    stepFocusRef.current?.focus();
  }, [open, step]);

  /** Tab cycle, scoped to this wizard's own wrapper.
   *
   *  Modal's X button sits in its header, OUTSIDE this wrapper, so it is not in
   *  the cycle. That is why the footer below always carries a real Cancel
   *  button: closing must never depend on reaching a control the trap excludes.
   *  Escape and the backdrop still close, both through the same guarded
   *  handler. */
  function handleKeyDown(ev: KeyboardEvent<HTMLDivElement>) {
    if (ev.key !== "Tab") return;
    const root = containerRef.current;
    if (!root) return;
    const nodes = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    if (nodes.length === 0) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const active = document.activeElement;
    if (ev.shiftKey) {
      if (active === first || !root.contains(active)) {
        ev.preventDefault();
        last.focus();
      }
      return;
    }
    if (active === last) {
      ev.preventDefault();
      first.focus();
    }
  }

  // ── Navigation ────────────────────────────────────────────────────────────

  /** Guarded against closing mid-submit — precedent BrokerDealsPage. All three
   *  of Modal's close routes (Escape, backdrop, X) funnel through this single
   *  prop, so a caller-side guard is complete rather than partial. */
  const handleRequestClose = useCallback(() => {
    if (busy) return;
    onClose();
  }, [busy, onClose]);

  function goNext(ev?: FormEvent) {
    ev?.preventDefault();
    if (!ready) return;
    setError(null);
    setStep("how");
  }

  function goBack() {
    setError(null);
    if (step === "duplicate") {
      // Back out of the CONFLICT, not out of the wizard. Every typed value
      // survives: the partner may want to correct the address, or leave it to
      // the colleague who already owns the invite. Clearing the warning is what
      // drops the third step from the stepper again — the server is re-asked on
      // the next attempt rather than the answer being cached here.
      setDuplicateWarning(null);
      setStep("how");
      return;
    }
    setStep("who");
  }

  function handleSend() {
    setError(null);
    setPendingAction("send");
    void send.submit(false);
  }

  function handleStartInPerson() {
    setError(null);
    setPendingAction("in-person");
    void startInPerson.submit(false);
  }

  /** Proceed over the warning, through the path the partner actually chose.
   *  The server records the override with the prior sender named — it is not a
   *  suppression. */
  function handleConfirmDuplicate() {
    setDuplicateWarning(null);
    if (pendingAction === "in-person") {
      void startInPerson.submit(true);
      return;
    }
    void send.submit(true);
  }

  const steps = duplicateWarning ? [...BASE_STEPS, CONFIRM_STEP] : BASE_STEPS;

  const errorLine = error ? (
    <p className="mt-space-3 mb-0 text-small text-status-error" role="alert">
      {error}
    </p>
  ) : null;

  const footer = (
    <div className="mt-space-4 flex flex-wrap items-center justify-between gap-space-2 border-t border-border-light pt-space-3">
      <Button
        type="button"
        variant="text"
        size="sm"
        onClick={handleRequestClose}
        disabled={busy}
        data-testid="prequal-wizard-cancel"
      >
        Cancel
      </Button>
      {step === "who" ? (
        <SubmitButton
          type="submit"
          variant="primary"
          size="sm"
          pending={false}
          disabled={!ready}
          data-testid="prequal-wizard-next"
        >
          Next
        </SubmitButton>
      ) : (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={goBack}
          disabled={busy}
          data-testid="prequal-wizard-back"
        >
          Back
        </Button>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={handleRequestClose}
      title="New Prequalification"
      description={`They'll see ${siteName}. When they finish, their result shows up in your completions.`}
      size="md"
    >
      <div
        ref={containerRef}
        onKeyDown={handleKeyDown}
        data-testid="prequal-wizard"
        className="flex flex-col"
      >
        <StatusStepper
          steps={steps}
          currentIndex={STEP_INDEX[step]}
          label="New prequalification progress"
          className="mb-space-4"
          data-testid="prequal-wizard-steps"
        />

        {step === "who" && (
          <form onSubmit={goNext} noValidate aria-label="Who is prequalifying">
            <div className="grid gap-space-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="prequal-invite-first" required>
                  First name
                </Label>
                <Input
                  id="prequal-invite-first"
                  ref={setStepFocus}
                  value={firstName}
                  onChange={(ev) => setFirstName(ev.target.value)}
                  disabled={busy}
                  autoComplete="off"
                />
              </div>
              <div>
                <Label htmlFor="prequal-invite-last" required>
                  Last name
                </Label>
                <Input
                  id="prequal-invite-last"
                  value={lastName}
                  onChange={(ev) => setLastName(ev.target.value)}
                  disabled={busy}
                  autoComplete="off"
                />
              </div>
              <div>
                <Label htmlFor="prequal-invite-email" required>
                  Email
                </Label>
                <Input
                  id="prequal-invite-email"
                  type="email"
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  placeholder="name@example.com"
                  disabled={busy}
                  autoComplete="off"
                />
              </div>
              <div>
                <Label htmlFor="prequal-invite-role">Track</Label>
                <select
                  id="prequal-invite-role"
                  className={SELECT_CLASS}
                  value={role}
                  onChange={(ev) => setRole(ev.target.value as PrequalRole)}
                  disabled={busy}
                >
                  <option value="Buyer">Buyer — how much they can borrow</option>
                  <option value="Seller">Seller — what their business supports</option>
                </select>
              </div>
            </div>

            {/* The gate is HERE, on Next, rather than on the two submits at the
                far end. An address is needed on both paths — the in-office
                start still mints a real invite, and an invite with no recipient
                is not one — so letting a partner choose a delivery method
                before telling them they cannot use either is the wrong order to
                find out. */}
            {!ready && (
              <p className="mt-space-3 mb-0 text-caption text-gray-mid">
                Their name and email are needed either way &mdash; the in-person
                start records the same details.
              </p>
            )}

            {errorLine}
            {footer}
          </form>
        )}

        {step === "how" && (
          <>
            <div className="grid gap-space-3 sm:grid-cols-2">
              <div className="flex flex-col rounded-md border border-border-light bg-white-pure p-space-3">
                <h3 className="m-0 font-display text-lead font-bold text-onyx">
                  Email them a link
                </h3>
                <p className="mt-space-1 mb-space-3 flex-1 text-caption text-gray-mid">
                  We&rsquo;ll email {email.trim() || "them"} a link to{" "}
                  <span className="font-semibold text-onyx">{siteName}</span>. They fill it
                  in whenever they like, and the result lands in your completions.
                </p>
                <SubmitButton
                  type="button"
                  ref={setStepFocus}
                  variant="primary"
                  size="sm"
                  pending={send.pending}
                  pendingLabel="Sending…"
                  disabled={busy}
                  onClick={handleSend}
                  data-testid="prequal-invite-send"
                >
                  <Mail size={14} strokeWidth={1.5} aria-hidden className="mr-space-1" />
                  Send invitation
                </SubmitButton>
              </div>

              {/* The same decision made a different way, so it sits beside the
                  first rather than under it. */}
              <div className="flex flex-col rounded-md border border-border-light bg-white-pure p-space-3">
                <h3 className="m-0 font-display text-lead font-bold text-onyx">
                  They&rsquo;re with me now
                </h3>
                <p className="mt-space-1 mb-space-3 flex-1 text-caption text-gray-mid">
                  The form opens here for you to drive &mdash; nothing is emailed,
                  and their answers are saved as you go.
                </p>
                <SubmitButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  pending={startInPerson.pending}
                  pendingLabel="Opening…"
                  disabled={busy}
                  onClick={handleStartInPerson}
                  data-testid="prequal-invite-start-in-person"
                >
                  <UserPlus size={14} strokeWidth={1.5} aria-hidden className="mr-space-1" />
                  Start in person
                </SubmitButton>
              </div>
            </div>

            {errorLine}
            <SubmittingHint pending={busy} message="One moment…" className="mt-space-3" />
            {footer}
          </>
        )}

        {step === "duplicate" && (
          <>
            {/* A colleague already invited this person. A WARNING, not a rule:
                two people at one firm chasing the same buyer is a coordination
                problem, and the second sender may well be the right one to
                proceed. The server's own message is rendered verbatim — a
                second, client-composed copy is exactly how the warning and the
                error on a refused retry drift apart. */}
            <div
              ref={setStepFocus}
              tabIndex={-1}
              className="rounded-md border border-status-warning/40 bg-status-warning/10 p-space-3"
              role="alert"
              data-testid="prequal-invite-duplicate-warning"
            >
              <p className="m-0 text-small text-onyx">{duplicateWarning}</p>
              <div className="mt-space-3">
                <SubmitButton
                  type="button"
                  variant="primary"
                  size="sm"
                  pending={busy}
                  // The path the partner chose, not a fixed verb. An in-office
                  // start opens a form; it sends nothing, and labelling it
                  // "Sending…" described the wrong action entirely.
                  pendingLabel={pendingAction === "in-person" ? "Opening…" : "Sending…"}
                  onClick={handleConfirmDuplicate}
                  data-testid="prequal-invite-duplicate-confirm"
                >
                  Send anyway
                </SubmitButton>
              </div>
            </div>

            {errorLine}
            <SubmittingHint pending={busy} message="One moment…" className="mt-space-3" />
            {footer}
          </>
        )}
      </div>
    </Modal>
  );
}
